import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, ApiError } from "@/lib/api";
import { BookingBrandHeader } from "@/components/BookingBrandHeader";
import { BookingStayCard } from "@/components/guest-portal/BookingStayCard";
import { GuestProfilePanel } from "@/components/guest-portal/GuestProfilePanel";
import { useLocale } from "@/i18n";
import { formatCurrency } from "@/lib/money";
import { useCampsiteBranding } from "@/lib/useCampsiteBranding";
import { useTouristIntegrationEnabled } from "@/lib/useTouristIntegrationEnabled";
import {
  clearGuestSession,
  loadGuestSession,
  saveGuestSession,
  type GuestProfile,
  type GuestSession,
} from "@/lib/guestAuth";
import type { BookingsResponse } from "@/lib/guestPortalTypes";

type PortalTab = "stays" | "profile";

function BookingSection({
  title,
  items,
  emptyText,
}: {
  title: string;
  items: BookingsResponse["upcoming"];
  emptyText: string;
}) {
  return (
    <section className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
      {items.length === 0 ? (
        <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50/50 px-4 py-6 text-center text-sm text-slate-500">
          {emptyText}
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((item) => (
            <BookingStayCard key={item.booking.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function GuestPortalPage() {
  const { messages, localePath } = useLocale();
  const bookingPath = localePath("/buchen");
  const loginPath = localePath("/login");
  const { siteName, logoUrl } = useCampsiteBranding();
  const { enabled: touristIntegrationEnabled, loading: touristConfigLoading } =
    useTouristIntegrationEnabled();
  const navigate = useNavigate();
  const [session, setSession] = useState<GuestSession | null>(() => loadGuestSession());
  const [bookings, setBookings] = useState<BookingsResponse | null>(null);
  const [profile, setProfile] = useState<GuestProfile | null>(session?.guest ?? null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<PortalTab>("stays");

  const loadPortal = useCallback(async () => {
    const current = loadGuestSession();
    if (!current) {
      navigate(loginPath, { replace: true });
      return;
    }
    setSession(current);
    setLoading(true);
    setError(null);
    try {
      const [me, list] = await Promise.all([
        api.get<GuestProfile>("/api/v1/public/guest/me"),
        api.get<BookingsResponse>("/api/v1/public/guest/bookings"),
      ]);
      setProfile(me);
      setBookings(list);
      const updated: GuestSession = { ...current, guest: me };
      saveGuestSession(updated);
      setSession(updated);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        clearGuestSession();
        navigate(loginPath, { replace: true });
        return;
      }
      setError(e instanceof ApiError ? e.message : messages.portal.loadError);
    } finally {
      setLoading(false);
    }
  }, [navigate, loginPath, messages.portal.loadError]);

  useEffect(() => {
    void loadPortal();
  }, [loadPortal]);

  const paymentOverview = useMemo(() => {
    if (!bookings) return { open: 0, paid: 0, currency: "EUR" };
    const all = [...bookings.upcoming, ...bookings.past];
    let open = 0;
    let paid = 0;
    let currency = "EUR";
    for (const item of all) {
      open += Number(item.payment.open_amount);
      paid += Number(item.payment.paid_amount);
      currency = item.booking.currency;
    }
    return { open, paid, currency };
  }, [bookings]);

  function handleLogout() {
    void api.post("/api/v1/public/guest/auth/logout").finally(() => {
      clearGuestSession();
      navigate(loginPath, { replace: true });
    });
  }

  function handleProfileSaved(me: GuestProfile) {
    setProfile(me);
    if (session) {
      const updated = { ...session, guest: me };
      saveGuestSession(updated);
      setSession(updated);
    }
  }

  if (!touristConfigLoading && touristIntegrationEnabled) {
    return <Navigate to={bookingPath} replace />;
  }

  if (!session) {
    return null;
  }

  return (
    <div className="mx-auto min-h-screen max-w-3xl px-4 py-8 pb-16">
      <div className="mb-2 rounded-2xl bg-gradient-to-br from-brand-700 via-brand-600 to-brand-800 px-6 py-8 text-white shadow-lg">
        <BookingBrandHeader
          siteName={siteName}
          logoUrl={logoUrl}
          title={messages.portal.title}
          subtitle={
            profile
              ? `Willkommen zurück, ${profile.first_name}!`
              : undefined
          }
          className="!mb-0 [&_h1]:text-white [&_p]:text-brand-100 [&_img]:brightness-0 [&_img]:invert"
        />
        {!loading && bookings ? (
          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-brand-100">
                {messages.portal.upcoming}
              </p>
              <p className="mt-1 text-2xl font-bold">{bookings.upcoming.length}</p>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-brand-100">
                {messages.fields.price}
              </p>
              <p className="mt-1 text-2xl font-bold">
                {formatCurrency(String(paymentOverview.paid), paymentOverview.currency)}
              </p>
            </div>
            <div className="rounded-xl bg-white/10 px-4 py-3 backdrop-blur-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-brand-100">
                {messages.portal.bookNow}
              </p>
              <p className="mt-1 text-2xl font-bold">
                {formatCurrency(String(paymentOverview.open), paymentOverview.currency)}
              </p>
            </div>
          </div>
        ) : null}
      </div>

      <div className="mb-6 flex flex-col gap-2 sm:flex-row">
        <Link to={bookingPath} className="bk-btn-primary flex-1 text-center">
          {messages.portal.bookNow}
        </Link>
        <button type="button" className="bk-btn-secondary flex-1" onClick={handleLogout}>
          {messages.portal.logout}
        </button>
      </div>

      <nav
        className="mb-6 flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm"
        aria-label={messages.portal.sectionsAria}
      >
        <button
          type="button"
          className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
            tab === "stays" ? "bg-brand-600 text-white shadow-sm" : "text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => setTab("stays")}
        >
          {messages.portal.upcoming}
        </button>
        <button
          type="button"
          className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-semibold transition ${
            tab === "profile"
              ? "bg-brand-600 text-white shadow-sm"
              : "text-slate-600 hover:bg-slate-50"
          }`}
          onClick={() => setTab("profile")}
        >
          {messages.portal.profile}
        </button>
      </nav>

      {error ? (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {loading ? (
        <p className="text-center text-sm text-slate-500">{messages.common.loading}</p>
      ) : tab === "stays" ? (
        <div className="space-y-10">
          <BookingSection
            title={messages.portal.upcoming}
            items={bookings?.upcoming ?? []}
            emptyText={messages.portal.upcomingEmpty}
          />
          <BookingSection
            title={messages.portal.past}
            items={bookings?.past ?? []}
            emptyText={messages.portal.pastEmpty}
          />
        </div>
      ) : profile ? (
        <div className="bk-card">
          <GuestProfilePanel profile={profile} onSaved={handleProfileSaved} />
        </div>
      ) : null}

      <p className="mt-10 text-center text-sm text-slate-500">
        <Link to={localePath("/")} className="text-brand-700 underline">
          {messages.login.toBooking}
        </Link>
      </p>
    </div>
  );
}
